// src/hooks/usePermissionCatalog.ts
import { useEffect, useMemo, useState } from 'react';
import {
  SYSTEM_STRUCTURE,
  ModuleConfig,
  MenuConfig,
  getMenuFieldFlags,
} from '../config/system-structure';
import {
  PermissionsServiceHelpers,
  PermissionCatalogEntry,
} from '../utils/api-helpers/PermissionsService.helpers';

type ResourceType = 'menu' | 'button' | 'action' | 'flag' | string | undefined;

/**
 * Construye una estructura similar a SYSTEM_STRUCTURE a partir del catálogo.
 * - Usa SYSTEM_STRUCTURE como base para nombres, paths y orden.
 * - Solo agrega botones/acciones/flags si aparecen en el catálogo.
 * - Si un permiso no existe en SYSTEM_STRUCTURE, se ignora (para mantener consistencia con la UI).
 */
function buildStructureFromCatalog(catalog: PermissionCatalogEntry[]): ModuleConfig[] {
  // Index base structure
  const moduleMap = new Map<string, ModuleConfig>();
  SYSTEM_STRUCTURE.forEach((mod) => {
    const filteredMenus =
      mod.id === 'orders' ? mod.menus.filter((menu) => menu.id !== 'sales-catalog') : mod.menus;
    moduleMap.set(mod.id, {
      ...mod,
      menus: filteredMenus.map((m) => ({
        ...m,
        mainButtons: [],
        rowActions: [],
        columnFlags: [],
        fieldFlags: [],
      })),
    });
  });

  const getMenu = (moduleId: string, menuId: string): MenuConfig | undefined => {
    const mod = moduleMap.get(moduleId);
    return mod?.menus.find((m) => m.id === menuId);
  };

  const pushPerm = (menu: MenuConfig, perm: PermissionCatalogEntry, resource: ResourceType) => {
    // Buscar en SYSTEM_STRUCTURE para verificar que la acción existe y obtener el nombre correcto
    const module = SYSTEM_STRUCTURE.find((m) => m.id === perm.module);
    if (!module) return; // Si el módulo no existe, ignorar

    const menuConfig = module.menus.find((m) => m.id === perm.menu);
    if (!menuConfig) return; // Si el menú no existe, ignorar

    let name: string | undefined;
    let found = false;
    let description: string | undefined = perm.description;

    // Verificar si la acción existe en SYSTEM_STRUCTURE
    if (resource === 'button' && menuConfig.mainButtons) {
      const button = menuConfig.mainButtons.find((b) => b.id === perm.action);
      if (button) {
        name = button.name;
        found = true;
      }
    } else if (resource === 'action' && menuConfig.rowActions) {
      const action = menuConfig.rowActions.find((a) => a.id === perm.action);
      if (action) {
        name = action.name;
        found = true;
      }
    } else if (resource === 'flag' && menuConfig.columnFlags) {
      const flag = menuConfig.columnFlags.find((f) => f.id === perm.action);
      if (flag) {
        name = flag.name;
        description = flag.description ?? perm.description;
        found = true;
      }
    } else if (resource === 'field') {
      const flag = getMenuFieldFlags(menuConfig).find((f) => f.id === perm.action);
      if (flag) {
        name = flag.name;
        description = flag.description ?? perm.description;
        found = true;
      }
    }

    // Si la acción NO existe en SYSTEM_STRUCTURE, ignorarla (no agregarla)
    if (!found) {
      return; // No agregar permisos que no estén en SYSTEM_STRUCTURE
    }

    // Si llegamos aquí, la acción existe en SYSTEM_STRUCTURE, usar su nombre
    if (resource === 'button') {
      (menu.mainButtons ??= []).push({
        id: perm.action,
        name: name!,
        action: perm.action,
      });
    } else if (resource === 'action') {
      (menu.rowActions ??= []).push({
        id: perm.action,
        name: name!,
        action: perm.action,
      });
    } else if (resource === 'flag') {
      (menu.columnFlags ??= []).push({
        id: perm.action,
        name: name!,
        action: perm.action,
        description,
      });
    } else if (resource === 'field') {
      // fieldFlagGroups / fieldFlagSections come from SYSTEM_STRUCTURE (spread);
      // flat fieldFlags for menus without groups/sections
      const inGroup = (menuConfig.fieldFlagGroups ?? []).some((g) =>
        g.flags.some((f) => f.id === perm.action),
      );
      const inSection = (menuConfig.fieldFlagSections ?? []).some((s) =>
        s.groups.some((g) => g.flags.some((f) => f.id === perm.action)),
      );
      if (!inGroup && !inSection) {
        (menu.fieldFlags ??= []).push({
          id: perm.action,
          name: name!,
          action: perm.action,
          description,
        });
      }
    }
    // resource 'menu' no necesita agregar acciones; la visibilidad de menú se maneja aparte.
  };

  catalog
    .filter((p) => p.isActive)
    .forEach((perm) => {
      const menu = getMenu(perm.module, perm.menu);
      if (!menu) return; // ignorar permisos que no estén en la estructura base
      pushPerm(menu, perm, perm.resource);
    });

  return Array.from(moduleMap.values());
}

export function usePermissionCatalog() {
  const [catalog, setCatalog] = useState<PermissionCatalogEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    PermissionsServiceHelpers.getCatalog()
      .then((data) => {
        if (!active) return;
        setCatalog(Array.isArray(data) ? data : []);
        setError(null);
      })
      .catch((err) => {
        if (!active) return;
        console.error('Error fetching permission catalog', err);
        setError('No se pudo cargar el catálogo de permisos');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const structure = useMemo<ModuleConfig[]>(() => {
    if (catalog && catalog.length > 0) {
      return buildStructureFromCatalog(catalog);
    }
    // Fallback: estructura estática
    return SYSTEM_STRUCTURE.map((mod) => ({
      ...mod,
      menus:
        mod.id === 'orders' ? mod.menus.filter((menu) => menu.id !== 'sales-catalog') : mod.menus,
    }));
  }, [catalog]);

  return {
    structure,
    loading,
    error,
  };
}
